import { useState, useEffect } from 'react';
import { db } from '@db/database';
import type { DcRuntimeViewModel } from '@api/bff/openapi/models';

type WorkOrderDetail = {
  companyName?: string | null;
  id?: string | null;
  globalId?: string | null;
};

type UseOfflineCalibrationsResult = {
  offlineCalibrations: Map<string, DcRuntimeViewModel | null>;
  isLoading: boolean;
};

export const useOfflineCalibrations = (
  isOnline: boolean,
  _jobNumber: string | undefined,
  details: WorkOrderDetail[]
): UseOfflineCalibrationsResult => {
  const [offlineCalibrations, setOfflineCalibrations] = useState<
    Map<string, DcRuntimeViewModel | null>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOfflineCalibrations = async () => {
      if (isOnline || details.length === 0) {
        return;
      }

      setIsLoading(true);

      try {
        const results = new Map<string, DcRuntimeViewModel | null>();

        for (const detail of details) {
          if (detail.globalId) {
            const cached = await db.cachedCalibrationRuntimes
              .where('woDetailGlobalId')
              .equals(detail.globalId)
              .first();

            results.set(detail.globalId, cached ?? null);
          }
        }

        setOfflineCalibrations(results);
      } catch (error) {
        console.error('Failed to fetch offline calibrations:', error);
        setOfflineCalibrations(new Map());
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfflineCalibrations();
  }, [isOnline, details]);

  return { offlineCalibrations, isLoading };
};
